import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, IconButton, Link, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tab, Tabs, TextField, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import { Refresh as RefreshIcon, OpenInNew as OpenIcon } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import grapeRulesService, {
  GrapeFinding, FindingSubject, CoverageRow,
} from '../../../services/grapeRulesService';

/**
 * GK-1 §4 — grape consistency findings, as their own review class.
 *
 * Two lists, because they are two different things:
 *   CONTRADICTIONS are live disagreements between records that both claim to
 *   be true — somebody must adjudicate each one.
 *   COVERAGE is the map of what we have not recorded. It is expected to be
 *   large, it is nobody's inbox, and its useful form is the WORKLIST: which
 *   appellations would pay off first, ranked by how many wines sit under
 *   them.
 *
 * The reviewer's only verb here is DISMISS, and it records a judgement about
 * the FINDING. Nothing on this screen edits a wine to make a flag go away.
 */

const KIND_COLOUR: Record<string, 'error' | 'warning' | 'info'> = {
  CONTRADICTION: 'error',
  PRECISION_INSUFFICIENT: 'warning',
  COVERAGE: 'info',
};

const FindingRow: React.FC<{
  finding: GrapeFinding;
  subject?: FindingSubject;
  grapeNames: Record<string, string>;
  onDismiss: (f: GrapeFinding) => void;
}> = ({ finding, subject, grapeNames, onDismiss }) => {
  const theme = useTheme();
  const rule = finding.details?.rule;
  const chain = (rule?.via ?? []).filter((v) => v.mode !== 'unknown').map((v) => v.regionName).join(' → ');

  return (
    <TableRow hover>
      <TableCell sx={{ verticalAlign: 'top' }}>
        <Chip size="small" label={finding.kind.replace('_', ' ').toLowerCase()} color={KIND_COLOUR[finding.kind] ?? 'info'} />
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top', minWidth: 200 }}>
        {subject ? (
          <>
            <Link
              component={RouterLink}
              to={`/admin/wine-names/edit/${finding.subjectId}`}
              variant="body2"
              sx={{ fontWeight: 600 }}
            >
              {subject.displayName}
            </Link>
            <Typography variant="caption" display="block" color="text.secondary">
              {subject.color}
            </Typography>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">{finding.subjectKind} {finding.subjectId}</Typography>
        )}
      </TableCell>

      {/* The wine's grapes, beside the region's rule — the reviewer compares
          two records, and never has to re-derive either. */}
      <TableCell sx={{ verticalAlign: 'top' }}>
        {(subject?.labelGrapes ?? []).length === 0
          ? <Typography variant="caption" color="text.secondary">no grapes recorded</Typography>
          : subject!.labelGrapes.map((g) => (
            <Chip key={g.id} size="small" label={g.name} sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top' }}>
        {rule ? (
          <>
            <Chip size="small" variant="outlined" label={rule.mode} sx={{ mb: 0.5 }} />
            <Box>
              {rule.grapes.length === 0
                ? <Typography variant="caption" color="text.secondary">—</Typography>
                : rule.grapes.map((g) => (
                  <Chip key={g} size="small" variant="outlined" label={grapeNames[g] ?? g} sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
            </Box>
            {chain && (
              <Typography variant="caption" color="text.secondary" display="block">
                from {chain}
              </Typography>
            )}
          </>
        ) : <Typography variant="caption" color="text.secondary">—</Typography>}
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top', maxWidth: 420 }}>
        <Typography variant="body2">{finding.reason}</Typography>
        {finding.rulePeriodRef && (
          <Typography variant="caption" color="text.secondary">
            rule period {finding.rulePeriodRef}
          </Typography>
        )}
      </TableCell>

      <TableCell sx={{ verticalAlign: 'top' }}>
        <Button size="small" onClick={() => onDismiss(finding)}>Dismiss</Button>
      </TableCell>
    </TableRow>
  );
};

const GrapeFindings: React.FC = () => {
  const theme = useTheme();
  const [tab, setTab] = useState(0);
  const [findings, setFindings] = useState<GrapeFinding[]>([]);
  const [subjects, setSubjects] = useState<Record<string, FindingSubject>>({});
  const [counts, setCounts] = useState({ contradictions: 0, coverage: 0, precision: 0 });
  const [grapeNames, setGrapeNames] = useState<Record<string, string>>({});
  const [coverage, setCoverage] = useState<CoverageRow[]>([]);
  const [coverageMeta, setCoverageMeta] = useState<{ totalRegions: number; regionsWithRules: number } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dismissing, setDismissing] = useState<GrapeFinding | null>(null);
  const [dismissReason, setDismissReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [page, work] = await Promise.all([
        grapeRulesService.listFindings({ limit: 200 }),
        grapeRulesService.coverage(40),
      ]);
      setFindings(page.items);
      setSubjects(page.subjects);
      setCounts(page.counts);
      setGrapeNames(page.grapeNames ?? {});
      setCoverage(work.items);
      setCoverageMeta({ totalRegions: work.totalRegions, regionsWithRules: work.regionsWithRules });
    } catch {
      setError('Could not load grape findings.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const confirmDismiss = async () => {
    if (!dismissing) return;
    try {
      await grapeRulesService.dismiss(dismissing._id, dismissReason.trim());
      setDismissing(null);
      setDismissReason('');
      await load();
    } catch {
      setError('Could not dismiss that finding.');
    }
  };

  const contradictions = findings.filter((f) => f.kind === 'CONTRADICTION' || f.kind === 'PRECISION_INSUFFICIENT');
  const coverageFindings = findings.filter((f) => f.kind === 'COVERAGE');

  const renderTable = (rows: GrapeFinding[], emptyLine: string) => (
    rows.length === 0
      ? <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{emptyLine}</Typography>
      : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Kind</TableCell>
                <TableCell>Wine name</TableCell>
                <TableCell>Its grapes</TableCell>
                <TableCell>The region&apos;s rule</TableCell>
                <TableCell>What disagrees</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((f) => (
                <FindingRow
                  key={f._id} finding={f} subject={subjects[f.subjectId]}
                  grapeNames={grapeNames} onDismiss={setDismissing}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5">Grape consistency</Typography>
        <IconButton onClick={load} aria-label="Refresh"><RefreshIcon /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap alignItems="baseline">
          <Box>
            <Typography variant="h6" color={counts.contradictions ? 'error.main' : 'inherit'}>
              {counts.contradictions}
            </Typography>
            <Typography variant="caption" color="text.secondary">contradictions — a person must decide</Typography>
          </Box>
          <Box>
            <Typography variant="h6">{counts.coverage}</Typography>
            <Typography variant="caption" color="text.secondary">coverage gaps — what we have not recorded</Typography>
          </Box>
          {coverageMeta && (
            <Box>
              <Typography variant="h6">
                {coverageMeta.regionsWithRules}/{coverageMeta.totalRegions}
              </Typography>
              <Typography variant="caption" color="text.secondary">regions with a rule recorded</Typography>
            </Box>
          )}
        </Stack>
      </Paper>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1 }}>
        <Tab label={`Contradictions (${contradictions.length})`} />
        <Tab label={`Coverage findings (${coverageFindings.length})`} />
        <Tab label="Where to curate next" />
      </Tabs>

      <Paper>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress size={24} /></Box>
        ) : (
          <>
            {tab === 0 && renderTable(
              contradictions,
              'Nothing contradicts. Note that a region with no recorded rule cannot produce a contradiction — '
              + 'see "Where to curate next".',
            )}
            {tab === 1 && renderTable(
              coverageFindings,
              'No coverage findings recorded yet.',
            )}
            {tab === 2 && (
              coverage.length === 0
                ? <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    Every region carrying wines has a rule recorded.
                  </Typography>
                : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2, pb: 1 }}>
                      Regions with wines but no recorded rule, most wines first. Recording the top of this
                      list buys the most checking for the least curation.
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Region</TableCell>
                            <TableCell>Hierarchy</TableCell>
                            <TableCell align="right">Wine names</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {coverage.map((r) => (
                            <TableRow key={r.regionId} hover>
                              <TableCell>{r.name}</TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">{r.hierarchyString}</Typography>
                              </TableCell>
                              <TableCell align="right">{r.wineNames}</TableCell>
                              <TableCell align="right">
                                <Button
                                  size="small" endIcon={<OpenIcon fontSize="small" />}
                                  component={RouterLink} to={`/admin/regions/${r.regionId}`}
                                >
                                  Record its rule
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )
            )}
          </>
        )}
      </Paper>

      <Dialog open={Boolean(dismissing)} onClose={() => setDismissing(null)} fullWidth maxWidth="sm">
        <DialogTitle>Dismiss this finding</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>{dismissing?.reason}</Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            This records a judgement about the finding. It does not change the wine, and the finding
            returns if the grapes or the region&apos;s rule move.
          </Alert>
          <TextField
            autoFocus fullWidth multiline minRows={2} label="Why is this not a problem?"
            value={dismissReason} onChange={(e) => setDismissReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDismissing(null)}>Cancel</Button>
          <Button variant="contained" disabled={!dismissReason.trim()} onClick={confirmDismiss}>
            Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GrapeFindings;
